import React, {useCallback, useEffect, useState} from 'react';
import {
  Anchor,
  Button,
  Heading,
  Icon,
  ListGroup,
  LoadingSpinner,
  TabNavigation,
  WindowSizeConsumer,
  addToast
} from '@albert-io/atomic';
import {AssignmentModelV3} from '@albert-io/models';
import {history} from 'client/history';
import sessionStore from 'client/Session/SessionStore';

import './global-assignments.scss';

import {invalidatePartialInterest} from 'resources/mandark.resource';

import MobileNavHeader from 'client/components/AssignmentsFolders/MobileNavHeader.react';

import {GlobalAssignmentFilterObj} from '../constants';

import SearchFilter from './SearchFilter.react';
import AssignmentFilters from './AssignmentsFilters.react';
import AssignmentCard from './AssignmentCard.react';
import {
  getPublishedGlobalAssignmentsQuery,
  getDraftGlobalAssignmentsQuery
} from './GlobalAssignments.queries';

const GlobalAssignments = () => {
  const [assignmentsList, setAssignmentsList] = useState<AssignmentModelV3[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>('');
  const [filters, setFilters] = useState<GlobalAssignmentFilterObj>({
    status: '',
    classes: {},
    shared: ''
  });
  const [filtersToUse, setFiltersToUse] = useState<GlobalAssignmentFilterObj>({
    status: '',
    classes: {},
    shared: ''
  });
  const [filtersOpen, setFiltersOpen] = useState<boolean>(false);
  const [sortByOption, setSortByOption] = useState<string>('updated_at');
  const [assignmentsLoading, setAssignmentsLoading] = useState<boolean>(false);
  // this only handles when you try loading more assignments
  const [moreAssignmentsLoading, setMoreAssignmentsLoading] = useState<boolean>(false);
  const [assignmentPage, setAssignmentPage] = useState<number>(1);
  const [totalNumPagesAssignments, setTotalNumPagesAssignments] = useState<number>(0);
  const [lengthAllAssignments, setLengthAllAssignments] = useState<number | null>(null);
  const [tab, setTab] = useState<string>(window.location.hash.substring(1) || 'published');

  const isDraftsTab = tab === 'drafts';

  const fetchAssignments = useCallback(
    async ({pageNum} = {pageNum: 1}) => {
      try {
        if (pageNum === 1) {
          setAssignmentsLoading(true);
          setTotalNumPagesAssignments(0);
        }
        const isAdmin = sessionStore.isSchoolAdministrator();
        const globalAssignmentsQuery = isDraftsTab
          ? getDraftGlobalAssignmentsQuery({
              name: searchTerm,
              sortOption: sortByOption === 'updated_at' ? '-updated_at' : 'name',
              classes: Object.keys(filtersToUse.classes || {}),
              page: pageNum
            })
          : getPublishedGlobalAssignmentsQuery({
              name: searchTerm,
              sortOption: sortByOption === 'updated_at' ? '-updated_at' : 'name',
              status: filtersToUse.status,
              classes: Object.keys(filtersToUse.classes || {}),
              page: pageNum,
              shared: filtersToUse.shared,
              isAdmin
            });
        const globalAssignments = await globalAssignmentsQuery.getResourcePromise();
        const totalNumPagesData = globalAssignmentsQuery
          .getResourceMetadata()
          .getIn(['page', 'total_pages'], 0);
        setTotalNumPagesAssignments(totalNumPagesData);
        invalidatePartialInterest({resourcePath: ['assignments_v3']});
        invalidatePartialInterest({resourcePath: ['draft_assignments_v1']});
        if (lengthAllAssignments === null) {
          setLengthAllAssignments(globalAssignments.toArray().length);
        }
        if (pageNum === 1) {
          setAssignmentsList(globalAssignments.toArray());
        } else {
          setAssignmentsList((prevAssignments) => [
            ...prevAssignments,
            ...globalAssignments.toArray()
          ]);
        }
        setAssignmentsLoading(false);
        setMoreAssignmentsLoading(false);
      } catch (_e) {
        setAssignmentsLoading(false);
        setMoreAssignmentsLoading(false);
        addToast({
          color: 'negative',
          title: "Couldn't fetch assignments",
          message: 'Error fetching assignments.'
        });
      }
    },
    [
      searchTerm,
      sortByOption,
      filtersToUse.classes,
      filtersToUse.status,
      filtersToUse.shared,
      lengthAllAssignments,
      isDraftsTab
    ]
  );

  const resetFilters = useCallback(() => {
    setFilters({status: '', classes: {}, shared: ''});
    setFiltersToUse({status: '', classes: {}, shared: ''});
    setSearchTerm('');
    setDebouncedSearchTerm('');
  }, []);

  const TAB_OPTIONS = [
    {value: 'published', label: 'Published'},
    {value: 'drafts', label: 'Drafts'}
  ];

  useEffect(() => {
    setAssignmentPage(1);
    fetchAssignments();
  }, [debouncedSearchTerm, filtersToUse, sortByOption]);

  const loadMoreAssignments = useCallback(() => {
    fetchAssignments({pageNum: assignmentPage + 1});
    setAssignmentPage((prevPage) => prevPage + 1);
    setMoreAssignmentsLoading(true);
  }, [fetchAssignments, assignmentPage]);

  useEffect(() => {
    if (window.location.hash !== `#${tab}`) {
      window.location.hash = tab;
      resetFilters();
    }
  }, [tab, resetFilters]);

  const hasFiltersOn = !!(
    Object.keys(filtersToUse.classes).length > 0 ||
    filtersToUse.shared ||
    filtersToUse.status ||
    searchTerm
  );

  return (
    <WindowSizeConsumer>
      {({breakpoints, viewportWidth}) => {
        const isMobile = viewportWidth <= breakpoints.m;
        return (
          <div className='global-assignments-wrapper'>
            <div className='assignments-folders__header-border'>
              {!isMobile ? (
                <div className='assignments-folders__header'>
                  <Heading color='inherit'>Assignments</Heading>
                  <div>
                    <TabNavigation
                      align='left'
                      className='global-assignments__tabs'
                      paddingLevel={0}
                    >
                      {TAB_OPTIONS.map((option) => (
                        <TabNavigation.Tab
                          key={option.value}
                          isActive={tab === option.value}
                          onClick={() => setTab(option.value)}
                        >
                          {option.label}
                        </TabNavigation.Tab>
                      ))}
                    </TabNavigation>
                  </div>
                </div>
              ) : (
                <MobileNavHeader
                  setTab={setTab}
                  title={`${isDraftsTab ? 'Draft ' : 'Published '} Assignments`}
                />
              )}
            </div>
            <div className='global-assignments__content'>
              <SearchFilter
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                setDebouncedSearchTerm={setDebouncedSearchTerm}
                setFiltersOpen={setFiltersOpen}
                filtersOpen={filtersOpen}
                filters={filters}
                sortByOption={sortByOption}
                setSortByOption={setSortByOption}
                isDraftsTab={isDraftsTab}
                setFilters={setFilters}
                setFiltersToUse={setFiltersToUse}
                isMobile={isMobile}
              />
              {filtersOpen && !isDraftsTab && (
                <AssignmentFilters
                  setFilters={setFilters}
                  filters={filters}
                  setFiltersToUse={setFiltersToUse}
                  assignmentsLoading={assignmentsLoading}
                  isMobile={isMobile}
                />
              )}
              {assignmentsLoading ? (
                <LoadingSpinner size={5} />
              ) : (
                <>
                  {!assignmentsList.length && !hasFiltersOn && (
                    <div className='global-assignments__no-results'>
                      <div className='global-assignments__no-results__header'>
                        You have no assignments
                      </div>
                      <div>Your assignments will appear on this page</div>
                      <Button
                        onClick={() =>
                          history.pushState(null, `/assignment-builder/create-assignment`)
                        }
                        className='u-mar-t_4'
                      >
                        New Assignment
                      </Button>
                    </div>
                  )}
                  {!assignmentsList.length && hasFiltersOn && (
                    <div className='global-assignments__no-results global-assignment__no-filtered-results'>
                      <div className='global-assignments__no-results__header'>
                        No assignments found
                      </div>
                      <Anchor underlined onClick={resetFilters}>
                        Clear filters
                      </Anchor>
                    </div>
                  )}
                  {!!assignmentsList.length && (
                    <ListGroup className='global-assignments__grid-container u-gap_space-x1-half flex flex-col'>
                      {assignmentsList.map((assignment) => (
                        <AssignmentCard
                          assignment={assignment}
                          setAssignmentsList={setAssignmentsList}
                          setLengthAllAssignments={setLengthAllAssignments}
                          key={assignment.id}
                        />
                      ))}
                    </ListGroup>
                  )}
                  {assignmentPage < totalNumPagesAssignments && (
                    <div className='flex justify-center'>
                      <Button
                        onClick={loadMoreAssignments}
                        className='u-mar-t_3'
                        disabled={moreAssignmentsLoading}
                      >
                        Show more
                        {moreAssignmentsLoading && (
                          <Icon spin icon='spinner' className='u-mar-l_1' />
                        )}
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        );
      }}
    </WindowSizeConsumer>
  );
};

export default GlobalAssignments;
